import React from 'react';
import { useField } from 'formik';
import { ProviderCurrency, ProviderTrustTier } from 'types/provider';
import { Header, Input, Table } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import DropdownField from 'components/form-fields/formik/DropdownField';
import PriceInput from 'components/form-fields/Price';

const ALLOWED_TRUST_TIERS_NUMBER = 5;

export function ProviderTrustTiersForm() {
  const { t } = useTranslation();

  const dropdownOptions = Array.from({ length: 5 }, (_, i) => ({
    key: i,
    text: t('editProvider.trustTiers.name', 'Tier {{TIER_NUMBER}}', {
      TIER_NUMBER: i,
    }),
    value: i,
  }));

  return (
    <div>
      <Header
        icon="sliders"
        content={t('editProvider.trustTiers.title', 'MSP Limits')}
        subheader={t(
          'editProvider.trustTiers.description',
          'configure tires for bussinesses and organizations'
        )}
        as="h4"
        dividing
      />

      <TrustTiersTable />
      <DropdownField
        fluid
        name="trustTiers.defaultForNewIndividuals"
        selection
        options={dropdownOptions}
        label={t(
          'editProvider.trustTiers.defaultForNewIndividuals',
          'Default Tier for New Individual Accounts'
        )}
      />

      <DropdownField
        name="trustTiers.defaultForNewOrganizations"
        fluid
        selection
        options={dropdownOptions}
        label={t(
          'editProvider.trustTiers.defaultForNewOrganizations',
          'Default Tier for New Organization Accounts'
        )}
      />
    </div>
  );
}

function TrustTiersTable() {
  const { t } = useTranslation();
  const trustTierField = useField<ProviderTrustTier[]>('trustTiers.tiers');
  const currenciesField = useField<ProviderCurrency[]>('currencies');

  // Just so I can rename the field
  const trustTiers = trustTierField[0];
  const currencies = currenciesField[0].value;

  const trustTiersHelper = trustTierField[2];

  // TODO: Hide when limits are not enabled

  if (!trustTiers.value || trustTiers.value.length === 0) {
    const defaultLimits = generateDefaultLimits(
      currencies.map((c) => c.currency)
    );
    trustTiersHelper.setValue(defaultLimits);
    // set value will trigger a re-render
    return <div></div>;
  }

  const rows: React.ReactElement[] = [];

  for (let tier = 0; tier < ALLOWED_TRUST_TIERS_NUMBER; tier++) {
    let showName = true;
    for (const currency of currencies) {
      rows.push(
        <Table.Row key={`row-${tier}-${currency.currency}`}>
          {showName && (
            <Table.Cell rowSpan={currencies.length}>
              {t('editProvider.trustTiers.TierName', 'Tier {{TIER_NUMBER}}', {
                TIER_NUMBER: tier,
              })}
            </Table.Cell>
          )}
          <Table.Cell>{currency.currency}</Table.Cell>
          <Table.Cell>
            <PriceInput
              value={getLimitValue(
                trustTiers.value,
                currency.currency,
                'individual',
                tier
              )}
              currency={currency.currency}
              onChange={(value) => {
                const newTrustTiers = trustTiers.value.map((e) => {
                  if (
                    e.currency === currency.currency &&
                    e.type === 'individual' &&
                    e.level === tier
                  ) {
                    return { ...e, limit: value };
                  }
                  return e;
                });

                trustTiersHelper.setValue(newTrustTiers);
              }}
            />
          </Table.Cell>
          <Table.Cell>
            <PriceInput
              value={getLimitValue(
                trustTiers.value,
                currency.currency,
                'organization',
                tier
              )}
              currency={currency.currency}
              onChange={(value) => {
                const newTrustTiers = trustTiers.value.map((e) => {
                  if (
                    e.currency === currency.currency &&
                    e.type === 'organization' &&
                    e.level === tier
                  ) {
                    return { ...e, limit: value };
                  }
                  return e;
                });

                trustTiersHelper.setValue(newTrustTiers);
              }}
            />
          </Table.Cell>
        </Table.Row>
      );
      showName = false;
    }
  }

  return (
    <Table celled structured>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>
            {t('editProvider.trustTiersHeaders.name', 'Name')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('editProvider.trustTiersHeaders.currency', 'Currency')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'editProvider.trustTiersHeaders.individualLimit',
              'Individual Limit'
            )}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'editProvider.trustTiersHeaders.organizationLimit',
              'Organization Limit'
            )}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>{rows}</Table.Body>
    </Table>
  );
}

function getLimitValue(
  trustTiers: ProviderTrustTier[],
  currency: string,
  type: 'individual' | 'organization',
  level: number
): number {
  const limit = trustTiers.find(
    (e) => e.currency === currency && e.type === type && e.level === level
  );
  return limit ? limit.limit : -1; // -1 means no limit set
}

function generateDefaultLimits(currencies: string[]): ProviderTrustTier[] {
  const tiers: ProviderTrustTier[] = [];

  // TODO: Replace with static default value
  for (let tier = 0; tier < ALLOWED_TRUST_TIERS_NUMBER; tier++) {
    for (const currency of currencies) {
      tiers.push({ level: tier, limit: -1, type: 'organization', currency });
      tiers.push({ level: tier, limit: -1, type: 'individual', currency });
    }
  }

  return tiers;
}
